/**
 * MobX store for Manage Returns list filters: search, drawer filters, quick status chips, and Open/Closed/All tab (status_group).
 * Initial search can be seeded from URL query `search_input` or, when that is absent, `orderId`.
 */
// eslint-disable-next-line
import { makeAutoObservable, runInAction } from "mobx";
import {GroupedOption, IOption} from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import RmaListService, { IErrorBucketOption, IErrors, IManualActionOption, IManualActions, IRmaListTab, IReturnReason, PAYMENT_STATUS_TYPE, RESERVED_ITEM_EXPIRATION_TYPE, RESTOCK_VALIDATED_TYPE } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import { IChangeable } from "../../../../stores/types";
import { RMA_QUICK_FILTERS } from "./rma-quick-filters";
import { isQuickFilterAllowedOnTab } from "./rma-tab-quick-filters";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import { IReportFiltersType } from "../../../../services/api/report-service";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import {
    formatChipCalendarDate,
    formatDate,
    isoRangeStringToCalendarRange,
} from "../../../../utilities/date-utilities";
import { ReservedItemDaysRemainingStore } from "../components/rma-list-filters-drawer/reserved-item-expiration-dropdown/reserved-item-days-remaining-store";
import ResolutionsPreset from "../../../policies/stores/presets/resolutions-preset";
import currentPortalStore from "../../../../stores/current-portal-store";
import {
    IRmaListFilterControlOption,
    IRmaListFilterControlOptionGroup,
} from "../../rma-list-page-new/components/rma-list-filter-control/rma-list-filter-control-portal/rma-list-filter-control-content/rma-list-filter-control-content";

export interface IRmaQuickFilterStatusOption {
    id: RMA_QUICK_FILTERS;
    title: string;
}

interface IRmaListFilters {
    search_input: string,
    search_exact_match: boolean,
    filter_resolution_id: number | null,
    filter_return_reason_id: number | null,
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE,
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE,
    filter_date_range: string,
    filter_reserve_item_expiration_input: string,
    filter_status?: string,
    filter_payment_status_type: PAYMENT_STATUS_TYPE,
    filter_manual_actions: string[],
    filter_error_labels: string[],
    status_group: IRmaListTab,
}
interface IRmaListFiltersChanges extends Partial<IRmaListFilters> { };

export interface IFiltersButtonstyle {
    variant: BUTTON_VARIANTS,
    color: string,
}

class RmaListFiltersStore implements IChangeable<IRmaListFilters> {
    private _isLoaded: boolean = false;
    private _isLoading: boolean = false;
    private _returnReasonOptions?: IReturnReason[] = [];
    private _manualActions?: IManualActions;
    private _errors?: IErrors;
    private _resolutionsPreset: ResolutionsPreset = {} as ResolutionsPreset;
    /** Sidebar resolution filter groups (excludes synthetic "All"); built once when filter options load. */
    private _resolutionFilterGroups: IRmaListFilterControlOptionGroup[] = [];
    /** Cached selected option for the sidebar chip label; updated when selection changes. */
    private _selectedResolutionOption: IRmaListFilterControlOption | null = null;
    /** Sidebar validated filter options (excludes synthetic "All"); built once on first access. */
    private _restockValidatedFilterOptions: IRmaListFilterControlOption[] = [];
    /** Cached selected option for the sidebar validated chip label; updated when selection changes. */
    private _selectedRestockValidatedOption: IRmaListFilterControlOption | null = null;
    private _model: IRmaListFilters;
    private _changes: IRmaListFiltersChanges = {};
    private _scope = 'dashboard-returns-page';
    private _reservedItemDaysRemainingStore: ReservedItemDaysRemainingStore = new ReservedItemDaysRemainingStore();

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = this._createModelFromQuery();
    }

    protected get _rmaListService(): RmaListService { return rootContainer.get<RmaListService>(TYPE.RmaListService); }

    public get searchInput() {
        return this._changes.search_input ?? this._model.search_input;
    }

    private get status(): string | undefined {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    public get searchURLParams(): URLSearchParams {
        const search = this.searchInput;
        const status = this.status;
        const newUrlSearchParams = new URLSearchParams();

        if (search) {
            newUrlSearchParams.set('search_input', search);
        }

        if (status) {
            newUrlSearchParams.set('filter_status', status);
        }

        return newUrlSearchParams;
    }

    public get searchExactMatch() {
        if (this._changes.search_exact_match === false) {
            return false;
        }
        return this._changes.search_exact_match ?? this._model.search_exact_match;
    }

    get isLoading() {
        return this._isLoading;
    }

    get areFilterOptionsLoaded() {
        return this._isLoaded;
    }

    get currentTab(): IRmaListTab {
        return this._changes.status_group ?? this._model.status_group;
    }

    get selectedResolutionId() {
        if (this._changes.filter_resolution_id === null) {
            return null;
        }
        return this._changes.filter_resolution_id ?? this._model.filter_resolution_id;
    }

    get selectedReturnReasonId() {
        if (this._changes.filter_return_reason_id === null) {
            return null;
        }
        return this._changes.filter_return_reason_id ?? this._model.filter_return_reason_id;
    }

    get restockValidatedType() {
        return this._changes.filter_restock_validated_type ?? this._model.filter_restock_validated_type;
    }

    /** Selected validated id for the redesigned sidebar filter; `null` when "All". */
    public get selectedRestockValidatedId(): RESTOCK_VALIDATED_TYPE | null {
        if (this.restockValidatedType === RESTOCK_VALIDATED_TYPE.ALL) {
            return null;
        }
        return this.restockValidatedType;
    }

    get reserveItemExpiration() {
        return this._changes.filter_reserve_item_expiration ?? this._model.filter_reserve_item_expiration;
    }

    get reserveItemExpirationInput() {
        return this._changes.filter_reserve_item_expiration_input ?? this._model.filter_reserve_item_expiration_input;
    }

    public get reserveItemExpirationFilterValid() {
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE && !this.reserveItemExpirationInput) {
            return false;
        }
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            const { from, to } = this._reservedItemDaysRemainingStore.getUpdatedModel();
            if (from === undefined || to === undefined || from >= to) {
                return false;
            }
        }
        return true;
    }

    get dateRange() {
        return this._changes.filter_date_range ?? this._model.filter_date_range;
    }

    /**
     * Chip label for the selected date range, formatted in the given IANA time zone.
     * Returns undefined when no range is set or parsing fails.
     */
    public getDateRangeSelectedChipLabel(timeZone: string): string | undefined {
        const range = isoRangeStringToCalendarRange(this.dateRange, timeZone);
        if (!range) {
            return undefined;
        }

        return `${formatChipCalendarDate(range.from)} - ${formatChipCalendarDate(range.to)}`;
    }

    /**
     * MM/DD/YYYY input pair for the selected date range in the given IANA time zone.
     * Returns empty strings when no range is set or parsing fails.
     */
    public getDateRangeInputValues(timeZone: string): { from: string; to: string } {
        const range = isoRangeStringToCalendarRange(this.dateRange, timeZone);
        if (!range) {
            return { from: "", to: "" };
        }

        return {
            from: formatDate(range.from),
            to: formatDate(range.to),
        };
    }

    /**
     * Count of applied (committed) wired sidebar filters that are non-default for Clear all (N).
     * Status counts when not ALL; date counts when a range is applied; resolution / return reason /
     * validated / reserved-item expiration when set.
     */
    public get appliedWiredSidebarFilterCount(): number {
        return [
            this._model.filter_status && this._model.filter_status !== RMA_QUICK_FILTERS.ALL,
            this._model.filter_date_range,
            this._model.filter_resolution_id,
            this._model.filter_return_reason_id,
            this._model.filter_restock_validated_type &&
                this._model.filter_restock_validated_type !== RESTOCK_VALIDATED_TYPE.ALL,
            this._model.filter_reserve_item_expiration &&
                this._model.filter_reserve_item_expiration !== RESERVED_ITEM_EXPIRATION_TYPE.ALL,
        ]
            .map(Boolean)
            .reduce((acc, curr) => acc + Number(curr), 0);
    }

    /**
     * Count of wired sidebar filters with uncommitted draft changes (status, date, resolution,
     * return reason, validated, reserved-item expiration). Used for Apply filters (N) and to
     * disable Apply when nothing is pending.
     */
    public get draftWiredSidebarFilterCount(): number {
        return [
            this._changes.filter_status !== undefined,
            this._changes.filter_date_range !== undefined,
            this._changes.filter_resolution_id !== undefined,
            this._changes.filter_return_reason_id !== undefined,
            this._changes.filter_restock_validated_type !== undefined,
            this._changes.filter_reserve_item_expiration !== undefined ||
                this._changes.filter_reserve_item_expiration_input !== undefined ||
                this._reservedItemDaysRemainingStore.hasChanges(),
        ]
            .map(Boolean)
            .reduce((acc, curr) => acc + Number(curr), 0);
    }

    get paymentStatus() {
        return this._changes.filter_payment_status_type ?? this._model.filter_payment_status_type;
    }

    get selectedQuickFilter() {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    /** Selected manual-action bucket values. */
    public get selectedManualActions(): string[] {
        return this._changes.filter_manual_actions ?? this._model.filter_manual_actions;
    }

    /** Whether the rms-api returned a manual-actions bucket (absent on older backends / when not applicable). */
    public get hasManualActionsBucket(): boolean {
        return !!this._manualActions;
    }

    /** Visible manual-action sections (the API already gates by the store's settings). */
    public get manualActionOptions(): IManualActionOption[] {
        return this._manualActions?.options ?? [];
    }

    /** Total distinct RMAs needing any visible manual action. */
    public get manualActionsTotal(): number {
        return this._manualActions?.total ?? 0;
    }

    /** True when every visible section is selected (drives the "All Manual Actions" state). */
    public get areAllManualActionsSelected(): boolean {
        const options = this.manualActionOptions;
        if (!options.length) {
            return false;
        }
        const selected = this.selectedManualActions;
        return options.every(option => selected.includes(option.value));
    }

    /**
     * Status chips for the redesigned sidebar, ordered for display and filtered by the active tab.
     * Manager-approval chips are included only when the portal requires manager approval
     * (`currentPortalStore.requireManagerApproval()`), which is an observable — keeping this as a
     * computed getter ensures the chip list re-renders when that setting changes.
     */
    public getVisibleQuickFilterStatuses(translate: (key: string) => string): IRmaQuickFilterStatusOption[] {
        const statusOptions: IRmaQuickFilterStatusOption[] = [
            { id: RMA_QUICK_FILTERS.PENDING, title: translate("PENDING") },
            ...(currentPortalStore.requireManagerApproval()
                ? [
                    { id: RMA_QUICK_FILTERS.PENDING_APPROVAL_ONLY, title: translate("PENDING_APPROVAL_ONLY") },
                    {
                        id: RMA_QUICK_FILTERS.PENDING_MANAGER_APPROVAL_ONLY,
                        title: translate("PENDING_MANAGER_APPROVAL_ONLY"),
                    },
                ]
                : []),
            { id: RMA_QUICK_FILTERS.APPROVED, title: translate("APPROVED") },
            { id: RMA_QUICK_FILTERS.IN_TRANSIT, title: translate("IN_TRANSIT") },
            { id: RMA_QUICK_FILTERS.RECEIVED, title: translate("RECEIVED") },
            { id: RMA_QUICK_FILTERS.DONE, title: translate("DONE") },
            { id: RMA_QUICK_FILTERS.REJECTED, title: translate("REJECTED") },
            { id: RMA_QUICK_FILTERS.CANCELED, title: translate("CANCELED") },
            { id: RMA_QUICK_FILTERS.SHIPMENT_REJECTED, title: translate("SHIPMENT_REJECTED") },
            { id: RMA_QUICK_FILTERS.REQUIRE_ATTENTION, title: translate("REQUIRE_ATTENTION") },
        ];

        return statusOptions.filter((status) => isQuickFilterAllowedOnTab(this.currentTab, status.id));
    }

    /** Selected error-bucket labels. */
    public get selectedErrorLabels(): string[] {
        return this._changes.filter_error_labels ?? this._model.filter_error_labels;
    }

    /**
     * Whether the rms-api returned an error bucket (absent on older backends / the legacy Laravel proxy).
     * Present-but-empty still counts: the bucket stays visible at 0 (shown dimmed), like its siblings.
     */
    public get hasErrorBucket(): boolean {
        return !!this._errors;
    }

    /** Error-bucket sub-rows (one per unresolved error label), aggregated by the rms-api. */
    public get errorOptions(): IErrorBucketOption[] {
        return this._errors?.options ?? [];
    }

    /** Total distinct open RMAs with at least one unresolved error. */
    public get errorsTotal(): number {
        return this._errors?.total ?? 0;
    }

    /** True when every error label is selected (drives the "All errors" state). */
    public get areAllErrorsSelected(): boolean {
        const options = this.errorOptions;
        if (!options.length) {
            return false;
        }
        const selected = this.selectedErrorLabels;
        return options.every(option => selected.includes(option.value));
    }

    private get _allOption() {
        return {
            label: dashboardTranslationsStore.getTranslation('ALL', this._scope),
            value: 0 /** Select dropdown options doesn't accept null */
        }
    }

    get resolutionsOptions(): GroupedOption[] {
        if (!this._isLoaded) {
            return [];
        }
        return [
            {category: '', options: [this._allOption]},
            ...this._resolutionsPreset.getOptionsPreset()
        ];
    }

    /** Options for the redesigned sidebar resolution filter (excludes the synthetic "All" option). */
    public get resolutionFilterGroups(): IRmaListFilterControlOptionGroup[] {
        return this._resolutionFilterGroups;
    }

    public get selectedResolutionLabel(): string | undefined {
        return this._selectedResolutionOption?.label;
    }

    get selectedResolutionValue(): IOption | undefined {
        if (!this.selectedResolutionId) {
            return this._allOption;
        }

        if (!this._isLoaded) {
            return undefined;
        }

        const selectedResolution = this._resolutionsPreset.getPresetById(this.selectedResolutionId);
        if (!selectedResolution) {
            return undefined;
        }

        return {
            label: selectedResolution.internal_name,
            value: selectedResolution.id
        };
    }

    get returnReasonsOptions() {
        if (!this._returnReasonOptions) {
            return [];
        }
        return [
            this._allOption,
            ...this._returnReasonOptions?.map(returnReason => (
                {
                    label: returnReason.name,
                    value: returnReason.id
                }
            ))
        ];
    }

    /** Options for the redesigned sidebar return-reason filter (excludes the synthetic "All" option). */
    public get returnReasonFilterOptions(): IRmaListFilterControlOption[] {
        return (this._returnReasonOptions ?? []).map((returnReason) => ({
            id: returnReason.id,
            label: returnReason.name,
        }));
    }

    public get selectedReturnReasonLabel(): string | undefined {
        const selectedOption = this.returnReasonFilterOptions.find(
            (o: IRmaListFilterControlOption) => o.id === this.selectedReturnReasonId,
        );

        if (!selectedOption) {
            return undefined;
        }

        return selectedOption.label;
    }

    public get selectedReturnReasonValue() {
        if (!this.selectedReturnReasonId) {
            return this._allOption;
        }
        return this.returnReasonsOptions?.find(res => res.value === this.selectedReturnReasonId)
    }

    get restockValidatedOptions() {
        return [
            {
                value: RESTOCK_VALIDATED_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.VALIDATED,
                label: dashboardTranslationsStore.getTranslation('VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.PARTIALLY_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('PARTIALLY_VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.NOT_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('NOT_VALIDATED', this._scope)
            }
        ];
    }

    /** Options for the redesigned sidebar validated filter (excludes the synthetic "All" option). */
    public get restockValidatedFilterOptions(): IRmaListFilterControlOption[] {
        this._ensureRestockValidatedFilterOptions();
        return this._restockValidatedFilterOptions;
    }

    public get selectedRestockValidatedLabel(): string | undefined {
        return this._selectedRestockValidatedOption?.label;
    }

    get restockValidatedValue() {
        return this.restockValidatedOptions?.find(res => res.value === this.restockValidatedType)
    }

    get reserverdItemExpirationOptions() {
        return [
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE,
                label: 'By date range'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING,
                label: 'By days remaining'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.EXPIRED,
                label: 'Expired'
            },
        ];
    }

    get reserverdItemExpirationValue() {
        return this.reserverdItemExpirationOptions?.find(res => res.value === this.reserveItemExpiration)
    }

    public get reservedItemDaysRemainingStore(): ReservedItemDaysRemainingStore {
        return this._reservedItemDaysRemainingStore;
    }

    get paymentStatusFilterOptions() {
        return [
            {
                value: PAYMENT_STATUS_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.EXPIRING,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_EXPIRING', this._scope)
            }
        ];
    }

    public setSearchCustomerId(value: string) {
        this._changes.search_input = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setSelectedResolutionId(value: number | null) {
        this._changes.filter_resolution_id = value;

        if (value === null) {
            this._selectedResolutionOption = null;
        } else {
            this._selectedResolutionOption = this._findResolutionFilterOption(value);
        }
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setSelectedReturnReasonId(value: number | null) {
        this._changes.filter_return_reason_id = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setRestockValidatedType(value: RESTOCK_VALIDATED_TYPE) {
        this._changes.filter_restock_validated_type = value;

        if (value === RESTOCK_VALIDATED_TYPE.ALL) {
            this._selectedRestockValidatedOption = null;
        } else {
            this._selectedRestockValidatedOption = this._findRestockValidatedFilterOption(value);
        }
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setPaymentStatusType(value: PAYMENT_STATUS_TYPE) {
        this._changes.filter_payment_status_type = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setReserverdItemExpiration(value: RESERVED_ITEM_EXPIRATION_TYPE) {
        this._changes.filter_reserve_item_expiration = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setReserverdItemExpirationInput(value: string | undefined) {
        this._changes.filter_reserve_item_expiration_input = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setDateRange(value: string) {
        this._changes.filter_date_range = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    public setSelectedQuickFilter(value: RMA_QUICK_FILTERS) {
        this._changes.filter_status = value;
        this._clearActionsStatusSelections();
        this._clearUnneededChanges();
    }

    /**
     * Toggles a single manual-action bucket. Manual actions are mutually exclusive with the
     * other filters, so selecting one clears the rest; clearing the last one leaves everything reset.
     */
    public toggleManualAction(value: string) {
        const current = this.selectedManualActions;
        const next = current.includes(value)
            ? current.filter(action => action !== value)
            : [...current, value];
        this._changes.filter_manual_actions = next;
        if (next.length) {
            this._clearNonManualActionFilters();
        }
        this._clearUnneededChanges();
    }

    /** "All Manual Actions": selects the union of visible sections, or clears them when all are already selected. */
    public toggleAllManualActions() {
        if (this.areAllManualActionsSelected) {
            this._changes.filter_manual_actions = [];
        } else {
            this._changes.filter_manual_actions = this.manualActionOptions.map(option => option.value);
            this._clearNonManualActionFilters();
        }
        this._clearUnneededChanges();
    }

    /**
     * Toggles a single error-bucket label. Like manual actions, the Error bucket is a top-level
     * "Actions status" selection mutually exclusive with every other filter (manual actions included), so
     * selecting one clears the rest; clearing the last one leaves everything reset.
     */
    public toggleErrorLabel(value: string): void {
        const current = this.selectedErrorLabels;
        const next = current.includes(value)
            ? current.filter(label => label !== value)
            : [...current, value];
        this._changes.filter_error_labels = next;
        if (next.length) {
            this._clearNonErrorFilters();
        }
        this._clearUnneededChanges();
    }

    /** "All errors": selects the union of every error label, or clears them when all are already selected. */
    public toggleAllErrors(): void {
        if (this.areAllErrorsSelected) {
            this._changes.filter_error_labels = [];
        } else {
            this._changes.filter_error_labels = this.errorOptions.map(option => option.value);
            this._clearNonErrorFilters();
        }
        this._clearUnneededChanges();
    }

    /** Clears search/status/drawer filters into the draft (shared by the manual-action and error mutual-exclusion). */
    private _clearBasicFilters(): void {
        this._changes.search_input = '';
        this._changes.filter_status = RMA_QUICK_FILTERS.ALL;
        this._changes.filter_resolution_id = null;
        this._selectedResolutionOption = null;
        this._changes.filter_return_reason_id = null;
        this._changes.filter_restock_validated_type = RESTOCK_VALIDATED_TYPE.ALL;
        this._selectedRestockValidatedOption = null;
        this._changes.filter_payment_status_type = PAYMENT_STATUS_TYPE.ALL;
        this._changes.filter_date_range = '';
        this._changes.filter_reserve_item_expiration = RESERVED_ITEM_EXPIRATION_TYPE.ALL;
        this._changes.filter_reserve_item_expiration_input = '';
        this._reservedItemDaysRemainingStore.discardChanges();
    }

    /** Clears every non-manual-action filter (basic filters + the sibling error bucket) when a manual action is picked. */
    private _clearNonManualActionFilters() {
        this._clearBasicFilters();
        this._clearErrorLabelsSelection();
    }

    /** Clears every non-error filter (basic filters + the sibling manual-actions bucket) when an error label is picked. */
    private _clearNonErrorFilters(): void {
        this._clearBasicFilters();
        this._clearManualActionsSelection();
    }

    /** Clears both Actions-status selections into the draft (mutual exclusion when a basic filter is picked). */
    private _clearActionsStatusSelections(): void {
        this._clearManualActionsSelection();
        this._clearErrorLabelsSelection();
    }

    /** Clears the manual-action selection into the draft (mutual exclusion when another filter is picked). */
    private _clearManualActionsSelection() {
        if (this.selectedManualActions.length) {
            this._changes.filter_manual_actions = [];
        }
    }

    /** Clears the error-label selection into the draft (mutual exclusion when another filter is picked). */
    private _clearErrorLabelsSelection(): void {
        if (this.selectedErrorLabels.length) {
            this._changes.filter_error_labels = [];
        }
    }

    /** Selects a status chip, or clears it back to ALL when the same chip is clicked again. */
    public toggleSelectedQuickFilter(status: RMA_QUICK_FILTERS) {
        const nextStatus = this.selectedQuickFilter === status ? RMA_QUICK_FILTERS.ALL : status;
        this.setSelectedQuickFilter(nextStatus);
    }

    /** Resets wired sidebar filters (status, date, resolution, return reason, validated, reserved item) to defaults. */
    public clearAll() {
        this.setSelectedQuickFilter(RMA_QUICK_FILTERS.ALL);
        this.setDateRange("");
        this.setSelectedResolutionId(null);
        this.setSelectedReturnReasonId(null);
        this.setRestockValidatedType(RESTOCK_VALIDATED_TYPE.ALL);
        this.setReserverdItemExpiration(RESERVED_ITEM_EXPIRATION_TYPE.ALL);
        this.setReserverdItemExpirationInput("");
        this._reservedItemDaysRemainingStore.reset();
    }

    public toggleSearchExactMatch() {
        this._changes.search_exact_match = !this.searchExactMatch;
        this._clearUnneededChanges();
    }

    /**
     * Switching the tab keeps the rest of the filters intact, but the currently selected status
     * chip may be incompatible with the new `status_group` (e.g. CLOSED tab + chip=open). The
     * server enforces this as a 400 cross-field check (rms-api: StatusGroupMatchesStatusConstraint),
     * so we proactively reset the chip to ALL when it would conflict.
     */
    public setCurrentTab(tab: IRmaListTab) {
        this._changes.status_group = tab;
        this._resetStatusFilterIfIncompatibleWithTab(tab);
        // The Actions-status buckets (Manual Actions and Errors) are only shown on the Open tab, so a
        // selection must not linger when the merchant moves to Closed/All.
        if (tab !== IRmaListTab.STATUS_GROUP_OPEN) {
            this._clearActionsStatusSelections();
        }
        this._clearUnneededChanges();
    }

    /**
     * Resets `filter_status` to ALL when the currently pending/applied chip is not allowed on the
     * given tab. Prevents an rms-api 400 from the `status_group` + `status` cross-field validator.
     */
    private _resetStatusFilterIfIncompatibleWithTab(tab: IRmaListTab) {
        const currentStatus = (this._changes.filter_status ?? this._model.filter_status) as RMA_QUICK_FILTERS | undefined;
        if (currentStatus && !isQuickFilterAllowedOnTab(tab, currentStatus)) {
            this._changes.filter_status = RMA_QUICK_FILTERS.ALL;
        }
    }

    public async loadFilterOptions() {
        if (this._isLoaded) {
            return;
        }

        try {
            this._isLoading = true;
            const filters = await this._rmaListService.fetchFilters();
            runInAction(() => {
                this._returnReasonOptions = filters.return_reasons;
                this._manualActions = filters.manual_actions;
                this._errors = filters.errors;
                this._resolutionsPreset = new ResolutionsPreset(filters.resolutions);
                this._resolutionFilterGroups = this._buildResolutionFilterGroups();
                this._syncSelectedResolutionOption();
                this._isLoading = false;
                this._isLoaded = true;
            });
        } catch (error) {
            runInAction(() => {
                this._isLoading = false;
            });
            console.log(error);
        }
    }

    private _buildResolutionFilterGroups(): IRmaListFilterControlOptionGroup[] {
        return this._resolutionsPreset
            .getOptionsPreset()
            .map((group) => ({
                title: typeof group.category === "string" ? group.category : "",
                options: group.options
                    .filter((option) => option.value !== 0)
                    .map((option) => ({
                        id: Number(option.value),
                        label: String(option.label ?? ""),
                    })),
            }))
            .filter((group) => group.options.length > 0);
    }

    private _findResolutionFilterOption(id: number): IRmaListFilterControlOption | null {
        return this._resolutionFilterGroups
            .flatMap((group) => group.options)
            .find((option) => option.id === id) ?? null;
    }

    private _syncSelectedResolutionOption() {
        const id = this.selectedResolutionId;
        if (id === null) {
            this._selectedResolutionOption = null;
        } else {
            this._selectedResolutionOption = this._findResolutionFilterOption(id);
        }
    }

    private _buildRestockValidatedFilterOptions(): IRmaListFilterControlOption[] {
        return this.restockValidatedOptions
            .filter((option) => option.value !== RESTOCK_VALIDATED_TYPE.ALL)
            .map((option) => ({
                id: option.value,
                label: option.label,
            }));
    }

    private _ensureRestockValidatedFilterOptions() {
        if (this._restockValidatedFilterOptions.length === 0) {
            this._restockValidatedFilterOptions = this._buildRestockValidatedFilterOptions();
        }
    }

    private _findRestockValidatedFilterOption(
        id: RESTOCK_VALIDATED_TYPE,
    ): IRmaListFilterControlOption | null {
        this._ensureRestockValidatedFilterOptions();
        return this._restockValidatedFilterOptions.find((option) => option.id === id) ?? null;
    }

    private _syncSelectedRestockValidatedOption() {
        const id = this.selectedRestockValidatedId;
        if (id === null) {
            this._selectedRestockValidatedOption = null;
        } else {
            this._selectedRestockValidatedOption = this._findRestockValidatedFilterOption(id);
        }
    }

    private _parseStatusGroupFromQuery(raw: string | null): IRmaListTab {
        if (raw === IRmaListTab.STATUS_GROUP_OPEN || raw === IRmaListTab.STATUS_GROUP_CLOSED || raw === IRmaListTab.STATUS_GROUP_ALL) {
            return raw;
        }
        return IRmaListTab.STATUS_GROUP_OPEN;
    }

    /** Prefer `search_input` when non-empty (after trim); otherwise fall back to trimmed `orderId`. */
    private _parseSearchInputFromQuery(params: URLSearchParams): string {
        const searchInput = params.get('search_input') ?? '';
        const orderId = params.get('orderId')?.trim() ?? '';

        let result = '';
        if (searchInput.trim()) {
            result = searchInput;
        } else if (orderId) {
            result = orderId;
        }

        return result;
    }

    private _createModelFromQuery(): IRmaListFilters {
        // Replace + with %2B before parsing to preserve literal + characters in search input
        // URLSearchParams decodes + as space per application/x-www-form-urlencoded standard
        const rawSearch = document.location.search.replace(/\+/g, '%2B');
        const params = new URLSearchParams(rawSearch);

        const filterStatus = params.get('filter_status');
        const searchInput = this._parseSearchInputFromQuery(params);
        const statusGroup = this._parseStatusGroupFromQuery(params.get("status_group"));

        return {
            search_input: searchInput,
            search_exact_match: false,
            filter_resolution_id: null,
            filter_return_reason_id: null,
            filter_restock_validated_type: RESTOCK_VALIDATED_TYPE.ALL,
            filter_date_range: '',
            filter_status: filterStatus || RMA_QUICK_FILTERS.OPEN,
            filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
            filter_reserve_item_expiration_input: '',
            filter_payment_status_type: PAYMENT_STATUS_TYPE.ALL,
            filter_manual_actions: [],
            filter_error_labels: [],
            status_group: statusGroup,
        }
    }

    public hasChanges(): boolean {
        const reservedItemDaysRemainingChanges = this._reservedItemDaysRemainingStore.hasChanges();
        return reservedItemDaysRemainingChanges || !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {};
        this._reservedItemDaysRemainingStore.discardChanges();
        this._syncSelectedResolutionOption();
        this._syncSelectedRestockValidatedOption();
    }

    public commitChanges(): void {
        this._reservedItemDaysRemainingStore.commitChanges();
        this._model = this.getUpdatedModel();
        this.discardChanges();
    }

    public getUpdatedModel(): IRmaListFilters {
        const updatedModel = { ...this._model, ...this._changes };
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            const daysRemainingModel = this._reservedItemDaysRemainingStore.getUpdatedModel();
            updatedModel.filter_reserve_item_expiration_input = `${daysRemainingModel.from}-${daysRemainingModel.to}`;
        }

        return updatedModel;
    }

    public getReportFilters(): IReportFiltersType {
        const filters: IReportFiltersType = {
            filter_is_restock_validated: this.restockValidatedType as string,
            filter_status: this.selectedQuickFilter as string,
            search_query: this.searchInput,
            is_exact_match: this.searchExactMatch ? '1' : '0'
        }
        if (this.selectedResolutionId) {
            filters['filter_resolution'] = this.selectedResolutionId.toString();
        }
        if (this.selectedReturnReasonId) {
            filters['filter_return_reason'] = this.selectedReturnReasonId.toString();
        }
        if (this.dateRange) {
            filters['filter_date'] = this.dateRange;
        }
        if (this.reserveItemExpiration) {
            filters['filter_reserve_item_expiration'] = this.reserveItemExpiration;
        }
        if (this.reserveItemExpirationInput) {
            filters['filter_reserve_item_expiration_input'] = this.reserveItemExpirationInput;
        }
        if (this.paymentStatus) {
            filters['filter_payment_status_type'] = this.paymentStatus;
        }
        return filters;
    }

    public hasActiveDrawerFilters(): boolean {
        const filters = this.getUpdatedModel();
        const {
            search_exact_match,
            search_input,
            filter_status,
            // Because status group is only used in Redesigned Manage returns list page
            // and this method (hasActiveDrawerFilters) is used for legacy page - excluding status_group here to avoid
            // incorrect output
            status_group: _omitStatusGroup,
            // Manual actions are a redesigned-page-only filter (and an empty array is truthy), so they
            // must not count as an active drawer filter on the legacy page.
            filter_manual_actions: _omitManualActions,
            // Error-bucket labels are likewise a redesigned-page-only filter (empty array is truthy).
            filter_error_labels: _omitErrorLabels,
            ...drawerFilters
        } = filters;
        return Object.values(drawerFilters).some(filter => (filter && filter !== RESTOCK_VALIDATED_TYPE.ALL));
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IRmaListFilters;
            const changed = this._changes[theKey];
            const applied = this._model?.[theKey];
            // filter_manual_actions is an array, so a strict === never matches even with identical
            // contents; compare by value to keep it from making the Apply button permanently dirty.
            const isEqual = Array.isArray(changed) && Array.isArray(applied)
                ? changed.length === applied.length && changed.every((item, index) => item === applied[index])
                : changed === applied;
            if (isEqual) {
                delete this._changes[theKey];
            }
        });
    }

}

export default RmaListFiltersStore;
